import { AvatarSize } from "@components/Avatar";
import { UserAvatar } from "@components/Avatar/UserAvatar";
import {
  Button,
  ButtonRole,
  ButtonSize,
  ButtonTargetKind,
} from "@components/Button";
import { EditableText } from "@components/EditableText";
import { FollowButton } from "@components/FollowButton";
import { FollowersList } from "@components/FollowersList";
import { FollowingList } from "@components/FollowingList";
import { Icon, IconDisplay, IconSize } from "@components/Icon";
import { Modal } from "@components/Modal";
import {
  ProfilePageHeaderCard,
  headerMetaWrapperStyles,
} from "@components/ProfilePageHeaderCard";
import { PublicAmountsIcon } from "@components/PublicAmountsIcon";
import { ShareButton } from "@components/ShareButton";
import { MAX_TEXT_INPUT_WIDTH } from "@components/TextInput";
import { ProfileAvatar } from "@components/User/ProfileAvatar";
import {
  UserPageTab,
  USER_ROUTE_NAME_FOR_TAB,
  USER_TAB_PARAM,
} from "@components/User/types";
import { VerifiedStatusIcon } from "@components/VerifiedStatusIcon";
import { CSSInterpolation } from "@emotion/css";
import { css } from "@emotion/react";
import { useRouter } from "next/router";
import React, { useMemo, useRef, useState } from "react";

import {
  UserResponse,
  FollowInfoResponse,
} from "@every.org/common/src/codecs/entities";
import { SafeInt } from "@every.org/common/src/codecs/number";
import {
  userBioTextCodec,
  MAX_USER_BIO_LEN,
} from "@every.org/common/src/codecs/text";
import {
  Currency,
  FollowRequestStatus,
} from "@every.org/common/src/entity/types";
import {
  ClientRouteName,
  getRoutePath,
  URLFormat,
  clientRouteMetas,
} from "@every.org/common/src/helpers/clientRoutes";
import { abbreviateNumber } from "@every.org/common/src/helpers/number";
import {
  cleanSocialNetworkHandle,
  SocialHandleKey,
  youtubeProfileHandle,
} from "@every.org/common/src/helpers/socialNetworks";
import {
  getUserFullNameOrPlaceholder,
  UNKNOWN_NAME,
} from "@every.org/common/src/helpers/username";
import { updateMeRouteSpec } from "@every.org/common/src/routes/me";

import { Link } from "src/components/Link";
import { updateAuthState } from "src/context/AuthContext/actions";
import { useLoggedInUserOrUndefined } from "src/context/AuthContext/hooks";
import {
  useEditableText,
  SaveTextActionResult,
  SaveTextActionStatus,
} from "src/hooks/useEditableText";
import { buttonCss as makeButtonCss } from "src/styles/button";
import { LinkAppearance } from "src/styles/link";
import { colorCssVars } from "src/theme/color";
import { MediaSize, cssForMediaSize } from "src/theme/mediaQueries";
import { horizontalStackCss } from "src/theme/spacing";
import { queryApi } from "src/utility/apiClient";
import { displayCurrencyValueInUserLocale } from "src/utility/currency";
import {
  DEFAULT_SHARE_IMAGE_CLOUDINARY_PARAMS,
  overlayAvatarOnDefaultOpengraphCloudinaryParams,
} from "src/utility/opengraph";

const AddLocationButton = () => (
  <Button
    title="Add your location"
    data-tname="UserProfile-AddLocation"
    role={ButtonRole.TEXT_SECONDARY}
    onClick={{
      kind: ButtonTargetKind.LINK,
      to: getRoutePath({
        name: ClientRouteName.PROFILE_SETTINGS,
        format: URLFormat.RELATIVE,
        tokens: {},
      }),
    }}
    css={{ display: "block" }}
    contentCss={[horizontalStackCss.xxs, { alignItems: "center" }]}
  >
    <Icon
      iconImport={() => import("@components/Icon/icons/PlusIcon")}
      size={IconSize.MEDIUM}
      display={IconDisplay.CURRENT_COLOR}
    />

    <span>Add your location</span>
  </Button>
);

const FollowData: React.FCC<{ data: number; label: string }> = ({
  data,
  label,
}) => {
  return (
    <dl
      css={[
        css`
          text-align: left;
        `,
        cssForMediaSize({
          min: MediaSize.LARGE,
          css: horizontalStackCss.xs,
        }),
        cssForMediaSize({
          max: MediaSize.X_SMALL,
          css: horizontalStackCss.xs,
        }),
      ]}
    >
      <dt
        css={css`
          color: var(${colorCssVars.text.secondary});
        `}
      >
        {label}
      </dt>
      <dd>{abbreviateNumber(data, 1)}</dd>
    </dl>
  );
};

const FollowMetadata: React.FCC<
  UserHeaderCardProps & { followerCount?: number }
> = ({
  user,
  followInfo,
  totalDonated,
  selectedTab,
  customStyle,
  followerCount,
}) => {
  const { pathname, query, push, back } = useRouter();

  const username = Array.isArray(query.username)
    ? query.username[0]
    : query.username;

  if (!username) {
    throw new Error("No username in params");
  }

  const visitedFollowModalDirectlyRef = useRef(
    [ClientRouteName.USER_FOLLOWING, ClientRouteName.USER_FOLLOWERS].find(
      (routeName) => pathname === clientRouteMetas[routeName].route
    ) !== undefined
  );
  const onFollowModalClosed = () => {
    if (visitedFollowModalDirectlyRef.current) {
      push(
        getRoutePath({
          name: USER_ROUTE_NAME_FOR_TAB[selectedTab],
          tokens: { username },
          format: URLFormat.RELATIVE,
        })
      );
      visitedFollowModalDirectlyRef.current = false;
    } else {
      back();
    }
  };

  const followingCount = followInfo && followInfo.followingCount;
  const viewPrivatePermitted =
    !user.isPrivate ||
    user.followedByCurrentUserStatus === FollowRequestStatus.ACCEPTED;

  return (
    <div
      css={[
        cssForMediaSize({
          max: MediaSize.X_SMALL,
          css: css`
            display: flex;
            flex-wrap: wrap;
          `,
        }),
        cssForMediaSize({
          max: MediaSize.SMALL,
          css: css`
            ${horizontalStackCss.s}
          `,
        }),
        cssForMediaSize({
          min: MediaSize.MEDIUM_SMALL,
          css: css`
            ${horizontalStackCss.xl}
          `,
        }),
        customStyle,
      ]}
    >
      {followingCount ? (
        viewPrivatePermitted ? (
          <React.Fragment>
            <Button
              title="Following"
              data-tname="viewFollowingButton"
              role={ButtonRole.UNSTYLED}
              onClick={{
                kind: ButtonTargetKind.LINK,
                to: getRoutePath({
                  name: ClientRouteName.USER_FOLLOWING,
                  // must be present if on user page
                  tokens: { username: user.username as string },
                  query: { [USER_TAB_PARAM]: selectedTab },
                  format: URLFormat.RELATIVE,
                }),
              }}
            >
              <FollowData label="Following:" data={followingCount} />
            </Button>
            <Modal
              isOpen={
                pathname ===
                clientRouteMetas[ClientRouteName.USER_FOLLOWING].route
              }
              headerText={`${user.firstName} Follows`}
              contentLabel="Following Modal"
              onRequestClose={onFollowModalClosed}
              showHeader
            >
              <FollowingList userId={user.id} />
            </Modal>
          </React.Fragment>
        ) : (
          <FollowData label="Following:" data={followingCount} />
        )
      ) : undefined}
      {followerCount ? (
        viewPrivatePermitted ? (
          <React.Fragment>
            <Button
              title="Followers"
              data-tname="viewFollowersButton"
              role={ButtonRole.UNSTYLED}
              onClick={{
                kind: ButtonTargetKind.LINK,
                to: getRoutePath({
                  name: ClientRouteName.USER_FOLLOWERS,
                  // must be present if on user page
                  tokens: { username: user.username as string },
                  query: { [USER_TAB_PARAM]: selectedTab },
                  format: URLFormat.RELATIVE,
                }),
              }}
            >
              <FollowData label="Followers:" data={followerCount} />
            </Button>
            <Modal
              isOpen={
                pathname ===
                clientRouteMetas[ClientRouteName.USER_FOLLOWERS].route
              }
              headerText={`${user.firstName}'s Followers`}
              contentLabel="Followers Modal"
              onRequestClose={onFollowModalClosed}
              showHeader
            >
              <FollowersList userId={user.id} followerCount={followerCount} />
            </Modal>
          </React.Fragment>
        ) : (
          <FollowData label="Followers:" data={followerCount} />
        )
      ) : undefined}
      {user.publicDonationAmount &&
      totalDonated &&
      totalDonated.amountInMinDenom > 0 ? (
        <React.Fragment>
          <dl
            css={[
              css`
                text-align: left;
              `,
              cssForMediaSize({
                min: MediaSize.LARGE,
                css: horizontalStackCss.xs,
              }),
              cssForMediaSize({
                max: MediaSize.X_SMALL,
                css: horizontalStackCss.xs,
              }),
            ]}
          >
            <dt
              css={css`
                color: var(${colorCssVars.text.secondary});
              `}
            >
              {"Donated: "}
            </dt>
            <dd>
              {displayCurrencyValueInUserLocale({
                minDenomCurrencyValue: {
                  currency: totalDonated.currency,
                  amountInMinDenom: totalDonated.amountInMinDenom,
                },
              })}
            </dd>
          </dl>
        </React.Fragment>
      ) : undefined}
    </div>
  );
};

interface UserHeaderCardProps {
  user: UserResponse;
  totalDonated?: { currency: Currency; amountInMinDenom: SafeInt } | undefined;
  followInfo?: FollowInfoResponse;
  selectedTab: UserPageTab;
  customStyle?: CSSInterpolation;
}

/**
 * Full page width card component that shows information about a user.
 */
export const UserHeaderCard: React.FCC<UserHeaderCardProps> = (props) => {
  const { user, followInfo } = props;
  const loggedInUser = useLoggedInUserOrUndefined();
  const isOwnProfile =
    !!loggedInUser && user.username === loggedInUser.username;
  const fullName = getUserFullNameOrPlaceholder(user);

  const title = (
    <div css={[horizontalStackCss.xs, { alignItems: "center" }]}>
      <div>{fullName}</div>
      <VerifiedStatusIcon
        verifiedStatus={user.verifiedStatus}
        size={IconSize.MEDIUM}
      />
      {user.publicDonationAmount && (
        <PublicAmountsIcon size={IconSize.MEDIUM} />
      )}
    </div>
  );

  const titleCompressor = fullName.length > 24 ? 2 : 1;

  const [followerCount, setFollowerCount] = useState<number | undefined>(
    followInfo && followInfo.followerCount
  );

  async function saveBioText(
    text: string | null
  ): Promise<SaveTextActionResult> {
    if (text === null || userBioTextCodec.is(text)) {
      if (text !== user.bioText) {
        const updatedUser = await queryApi(updateMeRouteSpec, {
          body: {
            bioText: text,
          },
          routeTokens: {},
          queryParams: {},
        });
        updateAuthState(updatedUser);
      }
      return {
        status: SaveTextActionStatus.SUCCESS,
      };
    }
    return {
      status: SaveTextActionStatus.ERROR,
      errorMessage: "Text is too long.",
    };
  }

  const bioTextInterface = useEditableText({
    initialText: user.bioText,
    saveAction: saveBioText,
    logTag: "UserHeaderCard-Bio",
  });

  const completeLinkedinHandle = new RegExp("^[^/]{2,}/.+");

  function addSlash(link: string) {
    if (!completeLinkedinHandle.test(link)) {
      link = `in/${link}`;
    }
    return link;
  }

  const socialMedia = useMemo(
    () =>
      [
        {
          name: "X",
          urlString: user.twitterHandle
            ? `https://x.com/${cleanSocialNetworkHandle(user.twitterHandle)}`
            : undefined,
          iconImport: () => import("@components/Icon/icons/XLogoCircleIcon"),
        },
        {
          name: "Facebook",
          urlString: user.facebookHandle
            ? `https://facebook.com/${cleanSocialNetworkHandle(
                user.facebookHandle
              )}`
            : undefined,
          iconImport: () => import("@components/Icon/icons/FacebookIcon"),
        },
        {
          name: "Instagram",
          urlString: user.instagramHandle
            ? `https://instagram.com/${cleanSocialNetworkHandle(
                user.instagramHandle
              )}`
            : undefined,
          iconImport: () =>
            import("@components/Icon/icons/InstagramLogoCircleIcon"),
        },
        {
          name: "LinkedIn",
          urlString: (() => {
            const urlString = cleanSocialNetworkHandle(
              user.linkedInHandle,
              SocialHandleKey.LINKEDIN
            );
            return urlString
              ? `https://linkedin.com/${addSlash(urlString)}`
              : undefined;
          })(),
          iconImport: () =>
            import("@components/Icon/icons/LinkedinLogoCircleIcon"),
        },
        {
          name: "YouTube",
          urlString: user.youtubeHandle
            ? `https://youtube.com/${youtubeProfileHandle(user.youtubeHandle)}`
            : undefined,
          iconImport: () =>
            import("@components/Icon/icons/YoutubeLogoCircleIcon"),
        },
        {
          name: "Bluesky",
          urlString: user.blueskyHandle
            ? `https://bsky.app/profile/${cleanSocialNetworkHandle(
                user.blueskyHandle,
                SocialHandleKey.BLUESKY
              )}`
            : undefined,
          iconImport: () =>
            import("@components/Icon/icons/BlueskyLogoCircleIcon"),
        },
      ].filter(({ urlString }) => !!urlString),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user]
  );

  return (
    <ProfilePageHeaderCard
      title={title}
      preTitle={`@${user.username}`}
      subtitle={
        user.locationAddress ||
        (isOwnProfile ? <AddLocationButton /> : undefined)
      }
      titleCompressor={titleCompressor}
      avatar={(avatarCss) =>
        isOwnProfile ? (
          <ProfileAvatar
            profileImageCloudinaryId={user.profileImageCloudinaryId}
            size={AvatarSize.MEDIUM}
            customCSS={avatarCss}
          />
        ) : (
          <UserAvatar
            user={user}
            size={AvatarSize.MEDIUM}
            disableLink
            customCSS={avatarCss}
          />
        )
      }
      metadata={
        <div
          css={[
            headerMetaWrapperStyles,
            cssForMediaSize({
              max: MediaSize.X_SMALL,
              css: css`
                display: flex;
                align-items: center;
                justify-content: space-between;
              `,
            }),
          ]}
        >
          {/* TODO: refactor using grid for avoid having FollowMetadata hidden by CSS */}
          <FollowMetadata
            customStyle={cssForMediaSize({
              min: MediaSize.LARGE,
              css: { display: "none" },
            })}
            {...props}
            followerCount={followerCount}
          />
          <div css={[horizontalStackCss.m, { alignItems: "center" }]}>
            {user.username && (
              <ShareButton
                aria-label={`Share user ${user.username}`}
                data-tname="shareUserButton"
                role={ButtonRole.TEXT_ONLY}
                hideText
                shareData={{
                  text: `Check out ${
                    isOwnProfile
                      ? "my"
                      : fullName === UNKNOWN_NAME
                      ? "this"
                      : fullName + "'s"
                  } giving profile on Every.org, please join or share these causes!`, // TODO(#4009) say which causes
                  url: getRoutePath({
                    name: ClientRouteName.USER,
                    tokens: { username: user.username },
                    format: URLFormat.ABSOLUTE,
                  }),
                }}
                instagramImageCloudinaryParams={
                  "profileImageCloudinaryId" in user &&
                  user.profileImageCloudinaryId
                    ? overlayAvatarOnDefaultOpengraphCloudinaryParams({
                        avatarCloudinaryId: user.profileImageCloudinaryId,
                      })
                    : DEFAULT_SHARE_IMAGE_CLOUDINARY_PARAMS
                }
              />
            )}
            {isOwnProfile ? (
              <Button
                title="Navigate to settings"
                data-tname="settings"
                onClick={{
                  kind: ButtonTargetKind.LINK,
                  to: getRoutePath({
                    format: URLFormat.RELATIVE,
                    name: ClientRouteName.SETTINGS,
                  }),
                }}
                role={ButtonRole.TEXT_ONLY}
                buttonCss={cssForMediaSize({
                  min: MediaSize.LARGE,
                  css: makeButtonCss(ButtonRole.TEXT_ONLY, ButtonSize.MEDIUM),
                })}
                size={ButtonSize.SMALL}
                icon={
                  <Icon
                    iconImport={() =>
                      import("@components/Icon/icons/SettingsIcon")
                    }
                    display={IconDisplay.CURRENT_COLOR}
                    size={IconSize.MEDIUM}
                  />
                }
              />
            ) : (
              <FollowButton
                size={ButtonSize.SMALL}
                buttonCss={cssForMediaSize({
                  min: MediaSize.LARGE,
                  css: makeButtonCss(ButtonRole.SECONDARY, ButtonSize.MEDIUM),
                })}
                toFollowUserId={user.id}
                setFollowerCount={setFollowerCount}
                followerCount={followerCount}
              />
            )}
          </div>
        </div>
      }
    >
      {isOwnProfile || user.bioText ? (
        <div>
          <EditableText
            data-tname={"user-bio"}
            viewCommentCss={css`
              max-width: ${MAX_TEXT_INPUT_WIDTH};
              color: var(${colorCssVars.text.secondary});
            `}
            maxCharCount={MAX_USER_BIO_LEN}
            disabled={!isOwnProfile}
            disableNewlines
            editableTextInterface={bioTextInterface}
            placeholder="Share a bit about yourself or why you give"
            saveOnBlur
            linkTarget="_blank"
          />
        </div>
      ) : undefined}
      {!!socialMedia.length && (
        <div css={horizontalStackCss.xs}>
          {socialMedia.map(({ name, urlString, iconImport }) => {
            return (
              urlString && (
                <Link
                  key={name}
                  to={urlString}
                  data-tname={`user${name}`}
                  appearance={LinkAppearance.HYPERLINK_SECONDARY}
                  target="_blank"
                >
                  <Icon
                    iconImport={iconImport}
                    display={IconDisplay.CURRENT_COLOR}
                    size={IconSize.MEDIUM}
                  />
                </Link>
              )
            );
          })}
        </div>
      )}
      <FollowMetadata
        customStyle={cssForMediaSize({
          max: MediaSize.MEDIUM,
          css: { display: "none" },
        })}
        {...props}
        followerCount={followerCount}
      />
    </ProfilePageHeaderCard>
  );
};
